import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type VideoHTMLAttributes,
} from "react";

function normalizeMediaUrl(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return trimmed;
}

/** Avoid using multi‑MB CMS originals as video posters (they become LCP). */
function toOptimizedPosterUrl(value?: string | null, width = 1080): string {
  const normalized = normalizeMediaUrl(value);
  if (!normalized) return "";
  if (normalized.includes("/_next/image")) return normalized;
  if (normalized.startsWith("/")) return normalized;
  return `/_next/image?url=${encodeURIComponent(normalized)}&w=${width}&q=75`;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  return reduced;
}

export type LazyVideoProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  "src" | "preload" | "autoPlay"
> & {
  src: string;
  poster?: string | null;
  /** Keep muted autoplay for in-view videos (homepage and course heroes). */
  autoPlay?: boolean;
  /**
   * How early to start loading relative to the viewport.
   * Hero media can use a larger margin; below-the-fold should stay modest.
   */
  rootMargin?: string;
  preload?: "none" | "metadata";
  /**
   * When true, keep the poster only until the user clicks/taps.
   * Used for heavy course-page heroes where viewport preload still fetches ~MB.
   */
  loadOnClick?: boolean;
  /**
   * Background-style hero: muted looping autoplay with no controls and no
   * click-to-play. Still defers the MP4 until in view (and after window load
   * when possible). Honours prefers-reduced-motion (poster only).
   */
  background?: boolean;
};

/**
 * Defers MP4 download until the element is near the viewport.
 * Hidden (display:none) responsive duplicates never intersect, so only the
 * visible mobile or desktop instance loads.
 * With loadOnClick, the file downloads only after a user click/tap.
 * With background, autoplays muted once loaded; no controls or click overlay.
 */
export default function LazyVideo({
  src,
  poster,
  className,
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  controls = false,
  rootMargin = "200px 0px",
  preload = "none",
  loadOnClick = false,
  background = false,
  onContextMenu,
  disablePictureInPicture,
  ...rest
}: LazyVideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const normalizedSrc = normalizeMediaUrl(src);
  const normalizedPoster = toOptimizedPosterUrl(poster);

  const effectiveLoadOnClick = background ? false : loadOnClick;
  const effectiveAutoPlay = background ? true : autoPlay;
  const effectiveMuted = background ? true : muted;
  const effectiveLoop = background ? true : loop;
  const effectivePlaysInline = background ? true : playsInline;
  const effectiveControls = background ? false : controls;
  // Background / autoplay paths: never fetch when the user prefers less motion.
  const blockForMotion =
    prefersReducedMotion && effectiveAutoPlay && !effectiveLoadOnClick;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.readyState === "complete") {
      setPageLoaded(true);
      return;
    }
    const onLoad = () => setPageLoaded(true);
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    // React can omit muted in SSR HTML; force the DOM property for autoplay.
    if (effectiveMuted) {
      node.muted = true;
      node.defaultMuted = true;
    }
  }, [effectiveMuted, shouldLoad]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node || shouldLoad || !normalizedSrc || effectiveLoadOnClick) return;
    if (blockForMotion) return;
    // Background heroes: wait until the page has loaded so the poster can be LCP.
    if (background && !pageLoaded) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    normalizedSrc,
    rootMargin,
    shouldLoad,
    effectiveLoadOnClick,
    blockForMotion,
    background,
    pageLoaded,
  ]);

  useEffect(() => {
    const node = videoRef.current;
    if (!node || !shouldLoad || !effectiveAutoPlay || blockForMotion) return;

    if (effectiveMuted) {
      node.muted = true;
      node.defaultMuted = true;
    }

    const tryPlay = () => {
      const playPromise = node.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay can be blocked; leave the poster visible with no controls.
        });
      }
    };

    if (node.readyState >= 2) {
      tryPlay();
      return;
    }

    node.addEventListener("loadeddata", tryPlay, { once: true });
    return () => node.removeEventListener("loadeddata", tryPlay);
  }, [
    effectiveAutoPlay,
    effectiveMuted,
    shouldLoad,
    normalizedSrc,
    blockForMotion,
  ]);

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLVideoElement>) => {
      event.preventDefault();
      onContextMenu?.(event);
    },
    [onContextMenu]
  );

  const handleActivate = useCallback(() => {
    if (effectiveLoadOnClick && !shouldLoad) {
      setShouldLoad(true);
    }
  }, [effectiveLoadOnClick, shouldLoad]);

  return (
    <video
      ref={videoRef}
      className={className}
      {...rest}
      src={shouldLoad && !blockForMotion ? normalizedSrc : undefined}
      poster={normalizedPoster || undefined}
      preload={shouldLoad && !blockForMotion ? preload : "none"}
      autoPlay={shouldLoad && effectiveAutoPlay && !blockForMotion}
      muted={effectiveMuted}
      loop={effectiveLoop}
      playsInline={effectivePlaysInline}
      controls={effectiveControls}
      disablePictureInPicture={background ? true : disablePictureInPicture}
      onContextMenu={handleContextMenu}
      onClick={effectiveLoadOnClick ? handleActivate : rest.onClick}
      onPlay={effectiveLoadOnClick ? handleActivate : rest.onPlay}
    />
  );
}
